import '/assets/styles/pages/listing.scss';
import { useEffect, useMemo, useState } from 'react';

// Functions
import isExact from '@wearetla/tla-essentials-tools/functions/is-exact';

// Partials
import { Input } from '/views/partials/forms';
import Loader from '/views/partials/loader';
import Pagination from '/views/partials/pagination';
import Eventbox from '/views/partials/eventbox';
import EmptyMessage from '/views/partials/empty-message';
// import Link from '@wearetla/tla-essentials-tools/partials/link';

// Context
import { useNavigator } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useLocations } from '/utilities/locations';
import { useGlobalState } from '@wearetla/tla-essentials-tools/utilities/global-state';
import {
	ListingProvider,
	useListing,
	decodeQueryString,
} from '@wearetla/tla-essentials-tools/utilities/listing';
import { useHead } from '@wearetla/tla-essentials-tools/utilities/head';
import { useTranslation } from '/utilities/i18n';

// Hooks

// Data
import listingFilters from '/data/listing-filters/events';
// import sortingOptions from '/data/sorting-options';

// Services
import eventServices from '/services/event';

const defaultQuery = {
	page: '1',
	order: 'asc',
	limit: '20',
	venue: '',
};

const allLocationsKey = 'tumu';

const getEvents = (query) => {
	return eventServices.getEvents(query);
};

const parseHead = (branch, t) => {
	return {
		title: branch?.city
			? t('{{branchTitle}} Etkinlikleri', { branchTitle: branch.title })
			: t('Tüm Etkinlikler'),
		description: branch?.city
			? t('Tüm {{branchTitle}} etkinlikleri.', { branchTitle: branch.title })
			: t('Tüm etkinlikler'),
	};
};

const EventsProvider = ({ match, ...props }) => {
	const { locations } = useLocations();
	const city = useMemo(() => {
		const foundLocation = locations.find((l) => (l.value || 'tumu') === match.params.location);

		return foundLocation?.value || '';
	}, [locations, match]);

	const externalParams = useMemo(
		() => ({
			location: city,
		}),
		[city]
	);

	return (
		<ListingProvider
			externalParams={externalParams}
			availableFilters={listingFilters}
			queryString={match?.params?.query}
			defaultQuery={defaultQuery}
		>
			<Events {...props} />
		</ListingProvider>
	);
};

const Events = ({ pageProps }) => {
	const { t } = useTranslation();
	const { locationOptions, venueOptions: rawVenueOptions } = useLocations();
	const { setNotFound } = useGlobalState();
	const { query, inputQuery, outputData, setFilter, paginate } = useListing();
	const { redirect } = useNavigator();
	const { setHead } = useHead();

	const [{ events, pagination, fetchedQuery }, setData] = useState(
		pageProps?.eventData ?? { events: null, fetchedQuery: null, pagination: null }
	);

	// const selectedSortOption = useMemo(() => {
	// 	return sortingOptions.find(o => o.value === inputQuery.order)
	// }, [inputQuery])

	const selectedVenue = useMemo(
		() => rawVenueOptions.find((f) => f.value === (query.venue || false)) ?? null,
		[rawVenueOptions, query]
	);

	const selectedLocation = useMemo(() => {
		return locationOptions.find((l) => {
			return (l.value || '') === inputQuery.location;
		});
	}, [inputQuery, locationOptions]);

	const venueOptions = useMemo(() => (
		selectedLocation?.value ? rawVenueOptions.filter(venue => !venue?.city || venue.city.value === selectedLocation.value) : rawVenueOptions
	), [rawVenueOptions, selectedLocation])

	useEffect(() => {
		setHead(parseHead(selectedLocation, t));
	}, [selectedLocation, t]);

	useEffect(() => {
		if (!isExact(fetchedQuery, inputQuery)) {
			setData({ events: null, pagination: null, fetchedQuery: inputQuery });
			getEvents(inputQuery)
				.then((eventData) => {
					setData({ ...eventData, fetchedQuery: inputQuery });
				})
				.catch(() => {
					setNotFound();
				});
		}
	}, [inputQuery, fetchedQuery]);

	useEffect(() => {
		const params = {
			location: selectedLocation?.value || allLocationsKey,
		};

		// Şehir değişmemişse query yokla, url değiştir.
		if ((selectedLocation?.value || '') === (outputData?.externalParams?.location ?? '')) {
			if (outputData.queryString?.length) {
				params.query = outputData.queryString;
			}
			redirect('eventsWithLocation', params);
		}
	}, [outputData, selectedLocation]);

	useEffect(() => {
		if (!selectedLocation && inputQuery?.location !== '') {
			setNotFound();
		}
	}, [selectedLocation, inputQuery]);

	return (
		<div className="listing-wrap wrapper">
			<h1 className="listing-title">{t('Etkinlikler')}</h1>

			<nav className="listing-filters">
				<div className="filters-left">
					{/* <div className="filters-locations cities">
						<strong className="locations-title">Nerede?</strong>
						{locationOptions.map((location) => (
							<Link
								href={location?.slug ? 'eventsWithLocation' : 'events'}
								params={location?.slug ? { location: location.slug } : undefined}
								key={location?.id ?? 'empty'}
								className={classNames('locations-item', { selected: (location.city ?? '') === inputQuery?.location })}>
								<span className="item-check"></span>
								<span>{location.shortTitle}</span>
							</Link>
						))}
					</div> */}

					<div className="filters-selectinput filters-city">
						<Input
							label={t('Şehir')}
							z={2}
							type="select"
							onChange={(city) => {
								if(city?.value !== selectedLocation?.value && (outputData.city ?? '' !== city?.value ?? '')) {
									redirect(
										city?.value ? 'eventsWithLocation' : 'events',
										city?.value ? { location: city.value } : {}
									);
								}
							}}
							value={selectedLocation}
							options={locationOptions}
						/>
					</div>
					<div className="filters-selectinput filters-venue">
						<Input
							label={t('Şube')}
							z={1}
							type="select"
							onChange={(c) => {
								setFilter('venue', c.value);
							}}
							value={selectedVenue}
							options={venueOptions}
						/>
					</div>
				</div>

				{/* <Input
					className="filters-sorting"
					type="select"
					value={selectedSortOption}
					onChange={(option) => {
						setFilter('order', option.value);
					}}
					label="Sıralama"
					options={sortingOptions} /> */}
			</nav>

			<div className="listing-results loader-container">
				<Loader loading={events === null} />
				{events && events.length > 0 && (
					<>
						<ul className="results-events">
							{events.map((event) => (
								<li className="listing-event" key={event.calendar_id}>
									<Eventbox
										analytics={{
											listId: `EVENTS_${(selectedLocation?.value || 'ALL').toUpperCase()}`,
											listName: t('Etkinlikler - {{locationTitle}}', {
												locationTitle: selectedLocation?.shortTitle ?? t('Tümü'),
											}),
										}}
										event={event}
									/>
								</li>
							))}
						</ul>
						<Pagination className="results-pagination" data={pagination} onPaginate={paginate} />
					</>
				)}
				{events?.length === 0 && <EmptyMessage>{t('Etkinlik Bulunamadı.')}</EmptyMessage>}
			</div>
		</div>
	);
};

EventsProvider.getServerProps = ({ match, sharedData }) => {
	return new Promise((resolve) => {
		const selectedLocation = (sharedData?.locations ?? []).find(
			(l) => (l.value ?? 'tumu') === match.params.location
		);

		const query = decodeQueryString({
			availableFilters: listingFilters,
			queryString: match.params.query,
			defaultQuery,
			externalParams: { location: selectedLocation?.slug ?? '' },
		});

		Promise.all([getEvents(query)])
			.then(([eventsPayload]) => {
				const eventData = {
					events: eventsPayload.events,
					fetchedQuery: query,
					pagination: eventsPayload.pagination,
				};

				resolve({
					pageProps: {
						eventData,
					}
				});
			})
			.catch(() => {
				resolve({});
			});
	});
};

export default EventsProvider;
